import { ColumnDef } from '@tanstack/react-table'
import type { RunbookTask } from '@/types/entities'
import { truncateToMinuteFloor } from '@/lib/dateUtils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, ArrowUp, Save, Copy, ImageIcon, X } from 'lucide-react'
import { useState, useRef, useEffect, memo } from 'react'
import { useToast } from '@/hooks/use-toast'
import { validateDependencies as validateDeps } from './utils/validateDependencies'
import { TranslatableText } from '@/components/ui/TranslatableText'

// Inline editable text cell with auto-expanding textarea
const EditableTextCell = memo(function EditableTextCell({
    value,
    onUpdate,
    placeholder,
    textColor,
    readOnly = false,
    showCopyIcon = false,
    translatable = false,
}: {
    value: string
    onUpdate: (value: string) => void
    placeholder: string
    textColor?: string
    readOnly?: boolean
    showCopyIcon?: boolean
    translatable?: boolean
}) {
    const { toast } = useToast()
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value || '')
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus()
            textareaRef.current.select()
            // Auto-expand
            textareaRef.current.style.height = 'auto'
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
        }
    }, [isEditing])

    useEffect(() => {
        // Only sync if not currently editing to prevent jumping
        if (!isEditing) {
            setLocalValue(value || '')
        }
    }, [value, isEditing])

    const handleSave = () => {
        if (localValue !== (value || '')) {
            onUpdate(localValue)
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setLocalValue(value || '')
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Stop propagation to prevent Swiper from capturing keyboard events
        e.stopPropagation()
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
        // Don't prevent default for other keys - allow normal typing
    }

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Stop propagation to prevent Swiper from interfering
        e.stopPropagation()
        setLocalValue(e.target.value)
        // Auto-expand
        e.target.style.height = 'auto'
        e.target.style.height = e.target.scrollHeight + 'px'
    }

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (value) {
            try {
                await navigator.clipboard.writeText(value)
                toast({
                    title: 'Copied',
                    description: 'Text copied to clipboard',
                })
            } catch (err) {
                toast({
                    title: 'Error',
                    description: 'Failed to copy text',
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div
            className="w-full flex items-start gap-1"
            onClick={(e) => {
                if (!isEditing && !readOnly) {
                    e.stopPropagation()
                    setIsEditing(true)
                }
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {isEditing && !readOnly ? (
                <textarea
                    ref={textareaRef}
                    value={localValue}
                    onChange={handleChange}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    onKeyPress={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onInput={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    className="min-h-8 w-full text-sm border rounded-md px-3 py-2 resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation()
                    }}
                    onMouseUp={(e) => {
                        e.stopPropagation()
                    }}
                    rows={1}
                    autoFocus
                />
            ) : (
                <>
                    <div
                        className={`min-h-8 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center ${readOnly ? '' : 'cursor-text hover:border-ring transition-colors'}`}
                        style={{ minWidth: 0 }}
                    >
                        {localValue ? (
                            <span className="text-sm w-full whitespace-pre-wrap break-words">{translatable ? <TranslatableText text={localValue} /> : localValue}</span>
                        ) : (
                            <span className={`text-sm w-full whitespace-pre-wrap break-words ${textColor || 'text-muted-foreground'}`}>{placeholder}</span>
                        )}
                    </div>
                    {showCopyIcon && value && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 mt-1 flex-shrink-0"
                            onClick={handleCopy}
                            title="Copy to clipboard"
                        >
                            <Copy className="h-3.5 w-3.5 text-blue-600" />
                        </Button>
                    )}
                </>
            )}
        </div>
    )
})

// Inline editable dependencies cell with validation
const EditableDependenciesCell = memo(function EditableDependenciesCell({
    value,
    onUpdate,
    placeholder,
    allTasks,
    currentTaskNo,
}: {
    value: string
    onUpdate: (value: string) => void
    placeholder: string
    allTasks: (RunbookTask & { _index: number })[]
    currentTaskNo: number | null
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value || '')
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setLocalValue(value || '')
        setError(null)
    }, [value])

    const validateDependencies = (input: string) => validateDeps(input, allTasks, currentTaskNo)

    const handleSave = () => {
        const validationError = validateDependencies(localValue)
        if (validationError) {
            setError(validationError)
            // Keep focus on input if empty/invalid - prevent navigation away
            if (inputRef.current) {
                inputRef.current.focus()
            }
            return // Don't save if invalid
        }

        if (localValue !== (value || '')) {
            onUpdate(localValue)
        }
        setError(null)
        setIsEditing(false)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Check if the new focus target is outside this component
        // If field is empty, prevent blur and keep focus
        const validationError = validateDependencies(localValue)
        if (validationError) {
            e.preventDefault()
            e.stopPropagation()
            // Keep focus on the input
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus()
                }
            }, 0)
            return
        }
        // Only call handleSave if valid
        handleSave()
    }

    const handleCancel = () => {
        setLocalValue(value || '')
        setError(null)
        setIsEditing(false)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        const newValue = e.target.value
        setLocalValue(newValue)

        // Validate on change to show errors in real-time
        const validationError = validateDependencies(newValue)
        setError(validationError)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        } else if (e.key === 'Tab') {
            // Prevent Tab navigation if field is empty/invalid
            const validationError = validateDependencies(localValue)
            if (validationError) {
                e.preventDefault()
                e.stopPropagation()
                // Keep focus on input
                if (inputRef.current) {
                    inputRef.current.focus()
                }
            }
            // If valid, allow Tab to proceed normally
        }
    }

    return (
        <div className="w-full">
            <div
                className="w-full"
                onClick={(e) => {
                    if (!isEditing) {
                        e.stopPropagation()
                        setIsEditing(true)
                    }
                }}
                onMouseDown={(e) => {
                    e.stopPropagation()
                }}
            >
                {isEditing ? (
                    <div className="w-full">
                        <input
                            ref={inputRef}
                            type="text"
                            value={localValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            onKeyPress={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                            onInput={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            className={`h-8 w-full text-sm border rounded-md px-3 py-2 focus-visible:outline-none focus-visible:ring-2 ${error
                                ? 'border-destructive focus-visible:ring-destructive'
                                : 'focus-visible:ring-ring'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation()
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                            }}
                            placeholder={placeholder}
                            autoFocus
                        />
                        {error && (
                            <div className="text-xs text-destructive mt-1 px-1">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center cursor-text hover:border-ring transition-colors"
                        style={{ minWidth: 0 }}
                    >
                        <span className="text-sm truncate w-full">{localValue || placeholder}</span>
                    </div>
                )}
            </div>
        </div>
    )
})

// Inline editable number cell
const EditableNumberCell = memo(function EditableNumberCell({
    value,
    onUpdate,
    placeholder,
}: {
    value: number | null
    onUpdate: (value: number | null) => void
    placeholder: string
}) {
    const [isEditing, setIsEditing] = useState(false)
    const [localValue, setLocalValue] = useState(value?.toString() || '')
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    useEffect(() => {
        setLocalValue(value?.toString() || '')
    }, [value])

    const handleSave = () => {
        const newValue = localValue === '' ? null : parseInt(localValue, 10)
        if (newValue !== value) {
            onUpdate(isNaN(newValue as number) ? null : newValue)
        }
        setIsEditing(false)
    }

    const handleCancel = () => {
        setLocalValue(value?.toString() || '')
        setIsEditing(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Stop propagation to prevent Swiper from capturing keyboard events
        e.stopPropagation()
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
        // Don't prevent default for other keys - allow normal typing
    }

    return (
        <div
            className="w-full"
            onClick={(e) => {
                if (!isEditing) {
                    e.stopPropagation()
                    setIsEditing(true)
                }
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            {isEditing ? (
                <Input
                    ref={inputRef}
                    type="number"
                    value={localValue}
                    onChange={(e) => {
                        e.stopPropagation()
                        setLocalValue(e.target.value)
                    }}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    className="h-8 w-full text-sm"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    min="0"
                />
            ) : (
                <div
                    className="h-8 w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-center cursor-text hover:border-ring transition-colors"
                    style={{ minWidth: 0 }}
                >
                    <span className="text-sm truncate w-full">{localValue || placeholder}</span>
                </div>
            )}
        </div>
    )
})

export interface RunbookTableProps {
    tasks: RunbookTask[]
    onTasksChange: (tasks: RunbookTask[]) => void
    onSave: () => void
    autoUpdate: boolean
    onAutoUpdateChange: (autoUpdate: boolean) => void
    isSaving?: boolean
}

export function createRunbookColumns(
    onTaskChange: (index: number, field: keyof RunbookTask, value: any) => void,
    onAddTask: () => void,
    onInsertTask: (index: number) => void,
    onDeleteTask: (index: number) => void,
    t: (key: string) => string,
    locale: string | undefined,
    allTasks: (RunbookTask & { _index: number })[],
    readOnly: boolean = false
): ColumnDef<RunbookTask & { _index: number }>[] {
    const effectiveLocale = locale || 'en-US'
    const columns: ColumnDef<RunbookTask & { _index: number }>[] = [
        {
            accessorKey: 'taskNo',
            header: t('objects.runbook.taskNo'),
            size: 70,
            minSize: 50,
            maxSize: 80,
            cell: ({ row }) => {
                const taskNo = row.getValue('taskNo') as number | null
                return (
                    <div
                        className="w-full flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <span className="text-sm font-bold text-black">
                            {taskNo ?? '-'}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'task',
            header: t('objects.runbook.task'),
            size: 500,
            minSize: 400,
            maxSize: 700,
            cell: ({ row }) => {
                const index = row.original._index
                const task = row.getValue('task') as string
                return (
                    <EditableTextCell
                        value={task || ''}
                        onUpdate={(value) => onTaskChange(index, 'task', value)}
                        placeholder={t('objects.runbook.task')}
                        readOnly={readOnly}
                        showCopyIcon={true}
                        translatable={true}
                    />
                )
            },
        },
        {
            accessorKey: 'commands',
            header: t('objects.runbook.commands'),
            size: 280,
            minSize: 200,
            maxSize: 400,
            cell: ({ row }) => {
                const index = row.original._index
                const commands = row.getValue('commands') as string
                return (
                    <EditableTextCell
                        value={commands || ''}
                        onUpdate={(value) => onTaskChange(index, 'commands', value)}
                        placeholder={t('objects.runbook.commands')}
                        textColor="text-muted-foreground"
                        readOnly={readOnly}
                        showCopyIcon={true}
                    />
                )
            },
        },
        {
            accessorKey: 'imagePath',
            header: t('objects.runbook.image', 'Image'),
            size: 70,
            minSize: 50,
            maxSize: 80,
            cell: ({ row }) => {
                const index = row.original._index
                const imagePath = row.getValue('imagePath') as string | null
                if (readOnly) {
                    return imagePath ? (
                        <div className="flex justify-center">
                            <img
                                src={`/images/${imagePath}`}
                                alt=""
                                className="h-8 w-8 rounded object-cover cursor-pointer hover:opacity-80"
                                onClick={(e) => { e.stopPropagation(); window.open(`/images/${imagePath}`, '_blank') }}
                            />
                        </div>
                    ) : null
                }
                return (
                    <div
                        className="flex justify-center items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        {imagePath ? (
                            <div className="relative group">
                                <img
                                    src={`/images/${imagePath}`}
                                    alt=""
                                    className="h-8 w-8 rounded object-cover cursor-pointer hover:opacity-80"
                                    onClick={() => onTaskChange(index, 'imagePath', '__PICK__')}
                                />
                                <button
                                    className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => onTaskChange(index, 'imagePath', null)}
                                >
                                    <X className="h-2.5 w-2.5" />
                                </button>
                            </div>
                        ) : (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => onTaskChange(index, 'imagePath', '__PICK__')}
                                title={t('objects.runbook.addImage', 'Add image')}
                            >
                                <ImageIcon className="h-4 w-4 text-blue-600" />
                            </Button>
                        )}
                    </div>
                )
            },
        },
        {
            accessorKey: 'duration',
            header: t('objects.runbook.duration'),
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const index = row.original._index
                const duration = row.getValue('duration') as number | null
                if (readOnly) {
                    return (
                        <div className="w-full text-right" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                            <span className="text-sm text-muted-foreground">
                                {duration != null ? `${duration}${t('objects.runbook.minutesShort') || 'm'}` : '-'}
                            </span>
                        </div>
                    )
                }
                return (
                    <EditableNumberCell
                        value={duration}
                        onUpdate={(value) => onTaskChange(index, 'duration', value)}
                        placeholder="0"
                    />
                )
            },
        },
        {
            accessorKey: 'taskOffset',
            header: t('objects.runbook.taskOffset') || 'Task Offset (min)',
            size: 120,
            minSize: 100,
            maxSize: 150,
            cell: ({ row }) => {
                const taskOffset = row.getValue('taskOffset') as number | null
                return (
                    <div
                        className="w-full text-right"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <span className="text-sm text-muted-foreground">
                            {taskOffset !== null && taskOffset !== undefined ? taskOffset : '-'}
                        </span>
                    </div>
                )
            },
        },
        {
            accessorKey: 'startTime',
            header: t('objects.runbook.startTime'),
            size: 180,
            minSize: 150,
            maxSize: 220,
            cell: ({ row }) => {
                const task = row.original
                const taskOffset = task.taskOffset as number | null
                const duration = task.duration as number | null
                
                // Calculate start time from TaskOffset using NOW() as base
                if (taskOffset === null || taskOffset === undefined) {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }

                try {
                    // Use NOW() as base time (minutes only), add TaskOffset (in minutes)
                    const baseTime = truncateToMinuteFloor(new Date())
                    const startTime = new Date(baseTime.getTime() + taskOffset * 60 * 1000)

                    if (isNaN(startTime.getTime())) {
                        return (
                            <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <span className="text-sm text-muted-foreground">-</span>
                            </div>
                        )
                    }

                    const dateStr = startTime.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = startTime.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return (
                        <div
                            className="w-full whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">
                                {dateStr} {timeStr}
                            </span>
                        </div>
                    )
                } catch {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }
            },
        },
        {
            accessorKey: 'finishTime',
            header: t('objects.runbook.finishTime'),
            size: 180,
            minSize: 150,
            maxSize: 220,
            cell: ({ row }) => {
                const task = row.original
                const taskOffset = task.taskOffset as number | null
                const duration = task.duration as number | null
                
                // Calculate finish time from TaskOffset + Duration using NOW() as base
                if (taskOffset === null || taskOffset === undefined || duration === null || duration === undefined) {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }

                try {
                    // Use NOW() as base time (minutes only), add TaskOffset + Duration (both in minutes)
                    const baseTime = truncateToMinuteFloor(new Date())
                    const finishTime = new Date(baseTime.getTime() + (taskOffset + duration) * 60 * 1000)

                    if (isNaN(finishTime.getTime())) {
                        return (
                            <div
                                className="w-full"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <span className="text-sm text-muted-foreground">-</span>
                            </div>
                        )
                    }

                    const dateStr = finishTime.toLocaleDateString(effectiveLocale, { month: 'short', day: 'numeric' })
                    const timeStr = finishTime.toLocaleTimeString(effectiveLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
                    return (
                        <div
                            className="w-full whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">
                                {dateStr} {timeStr}
                            </span>
                        </div>
                    )
                } catch {
                    return (
                        <div
                            className="w-full"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }
            },
        },
        {
            accessorKey: 'dependencies',
            header: t('objects.runbook.dependencies'),
            size: 180,
            minSize: 150,
            maxSize: 250,
            cell: ({ row }) => {
                const index = row.original._index
                const taskNo = row.original.taskNo
                const dependencies = row.getValue('dependencies') as string
                const isFirstTask = taskNo === 1

                if (isFirstTask) {
                    return (
                        <div
                            className="h-8 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">{t('objects.runbook.noDependencies')}</span>
                        </div>
                    )
                }

                if (readOnly) {
                    return (
                        <div
                            className="h-8 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm flex items-center"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-muted-foreground">{dependencies || '-'}</span>
                        </div>
                    )
                }

                return (
                    <EditableDependenciesCell
                        value={dependencies || ''}
                        onUpdate={(value) => onTaskChange(index, 'dependencies', value)}
                        placeholder={t('objects.runbook.dependencies')}
                        allTasks={allTasks}
                        currentTaskNo={taskNo}
                    />
                )
            },
        },
    ]

    // Only add actions column if not read-only
    if (!readOnly) {
        columns.push({
            id: 'actions',
            header: () => (
                <div className="text-right w-full">
                    {t('common.actions')}
                </div>
            ),
            size: 150,
            minSize: 120,
            maxSize: 180,
            cell: ({ row }) => {
                const index = row.original._index
                return (
                    <div
                        className="flex items-center justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onInsertTask(index)
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                            }}
                            title={t('objects.runbook.insertAbove')}
                        >
                            <ArrowUp className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-500 hover:text-red-600"
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onDeleteTask(index)
                            }}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                            }}
                            title={t('objects.runbook.delete')}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        })
    }

    return columns
}

